import { escapeHtml, emailSplit, dropHeaderFragment } from '../ui.js';
import { findDeadline, longDay } from '../mail-dates.js';
import { quoteCut, legalCut, signatureCut, splitPoints, stripQuoteHeader, quotedSentAt } from '../mail-fold.js';
import { renderCompose } from './mail-compose.js';
import {
  parseName, extractEmail, dirOf, toDate, stamp, startOfWeek,
  entryHtml, bucketHtml, bodyPara,
} from './mail-correspondence.js';

// MAIL — THE SHARED READER (design handoff C, artboard 1a) + the shared LIST
// (artboard 1b). "One reader, not two": Track's good email surface lived in the
// pilot-gated Files/Mail module while enquiries and project detail used a crude
// modal — raw address twice, To/Cc dumped into the body, the signature as body
// copy, no attachments, and only an "Open in Gmail" escape. This module is the
// ONE reader those surfaces now open, built on mail-correspondence.js's parsing
// (imported, not copied) and mail-compose.js for Reply / Reply all / Forward.
//
// Deliberately NO "Open in Gmail" link anywhere — the Files/Mail module's
// binding rule, now applied everywhere.
//
// House rendering-safety rule 0: every data value renders through escapeHtml();
// data-built hrefs would go through safeUrl() (none are built here). Stored
// bodies are PLAIN TEXT and render escaped; a live-Gmail HTML body renders ONLY
// through DOMPurify inside a sandboxed no-scripts iframe (same allowlist as
// views/mail-thread.js / views/google-panels.js) — if the sanitiser is missing
// we fall back to escaped plain text, never raw HTML.
//
// HONESTY RULES (from the handoff's "What this needs" card): attachments[],
// recipients[], bodyParts and dates[] mostly do not exist on stored entries yet.
// - Attachment chips render ONLY from real attachment data (live Gmail thread
//   messages carry it; projectEmails rows do not) — never fabricated.
// - "Track read a deadline" IS built (27/08, email canvas turn 2): mail-dates.js
//   reads a deadline the sender stated and the card OFFERS it. It is never
//   applied on its own — the button writes, nothing else does — and the card
//   always shows the phrase the date came from so the reader can judge it.
//   The rest of the Follow-up group (calendar, instruction, assign) lives in
//   the ··· menu, which is turn 2b and not built.
// - bodyParts is derived best-effort client-side: emailSplit() (ui.js) folds
//   the signature; quote markers fold forwarded/quoted history.

// ---- tokens / small style consts ------------------------------------------
const MONO = 'font-family:var(--font-technical);';
// The header's controls: 34px squares with a hairline, per artboard 2a.
const SQ = 'min-width:34px;height:34px;border-radius:8px;border:1px solid var(--hair);display:flex;align-items:center;justify-content:center;flex:none;font-size:14px;';
const EYEBROW = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;';
const BACKDROP = 'position:fixed;inset:0;z-index:190;background:rgba(var(--scrim-rgb), 0.34);display:flex;align-items:flex-start;justify-content:center;padding:48px 24px;overflow:auto;';
// (z 190: mail-compose's own backdrop is 200, so Reply opens ON TOP of the reader.)

const pad2 = (n) => String(n).padStart(2, '0');
const NUMWORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const numWord = (n) => NUMWORDS[n] || String(n);
const words = (t) => String(t || '').trim().split(/\s+/).filter(Boolean).length;
const quotedNote = (q, vm, at) => `${vm && vm.name ? vm.name + ', ' : ''}`
  + `${at ? stamp(at) + ' \u00b7 ' : ''}the original request, ${words(q)} words`;
const footerNote = (t) => `${words(t)} words of the sender's standard footer`;

// "The claim number, the file reference and the scheme code come out of the
// subject as mono chips. What is left is what the email is about." (2a)
// A reference is a token carrying a digit and no lower case — a claim number, a
// file ref, a scheme code. Ordinary words and Title Case never match.
const REF_TOKEN = /\b(?=[A-Z0-9][A-Z0-9._\/-]*\d)[A-Z0-9][A-Z0-9._\/-]{4,}\b/g;
function subjectRefs(subject) {
  const full = String(subject || '').trim();
  let rest = full;
  const refs = [];
  // A bracketed reference is ONE chip: "[KEN-LEGAL.FID48845919]".
  rest = rest.replace(/\[([^\]]{4,60})\]/g, (_, r) => { refs.push(r.trim()); return ' '; });
  (rest.match(REF_TOKEN) || []).forEach((r) => { refs.push(r); rest = rest.replace(r, ' '); });
  rest = rest.replace(/\s{2,}/g, ' ').replace(/\s*[:;,\u2013\u2014-]\s*$/, '').trim();
  // A subject that packs the claim, the site and the scheme after a colon keeps
  // the part before it; the whole line stays one hover away.
  const short = rest.split(/\s+[:\u2013\u2014]\s+/)[0].replace(/\s*[-,]\s*$/, '').trim();
  return { refs: [...new Set(refs)], rest: short || full, full, trimmed: !!short && short.length < full.length };
}
const ddmm = (d) => (d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}` : '');

function fmtSize(n) {
  n = Number(n) || 0;
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
const initialsOf = (name) => String(name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// Strip a stacked "Re: Fwd: Fw:" prefix run — "forwarded" is said in words in
// the header meta line instead (C1: "the Fwd: prefix is dropped").
const bareSubject = (s) => String(s || '').replace(/^\s*((re|fwd?|fw)\s*:\s*)+/i, '').trim();
const isFwdSubject = (s) => /^\s*(fwd?|fw)\s*:/i.test(String(s || ''));

// ---- recipients ------------------------------------------------------------
// Parse a To/Cc header string into [{ name, email }]. Best-effort split that
// keeps quoted display names ("Salvesen, Claire") and <a@b> groups intact.
export function parseRecipients(to) {
  const s = String(to || '').trim();
  if (!s) return [];
  const parts = s.match(/(?:"[^"]*"|<[^>]*>|[^,])+/g) || [];
  return parts.map((p) => p.trim()).filter(Boolean)
    .map((p) => ({ name: parseName(p), email: extractEmail(p) }))
    .filter((r) => r.name !== '—' || r.email);
}

// ---- body folding (bodyParts, derived) --------------------------------------
// Quote markers: forwarded blocks and reply chains. Conservative like
// emailSplit — a marker only cuts when it lands well into the text.
const QUOTE_MARKERS = [
  /\n[>\s]*On [^\n]{0,200}wrote:\s*\n/,       // "On 07/08/2026, X wrote:"
  /\n-*\s*Begin forwarded message:/i,          // Apple Mail
  /\n-{3,}\s*(Original|Forwarded) Message\s*-{3,}/i, // Outlook
  /\n_{10,}\s*\n\s*From:/,                     // Outlook divider + header block
  /\n(>\s*){1,}From:\s/,                       // quoted header block
  // A bare Outlook header block — the commonest reply chain in our mail, and
  // the one that used to render inline because it carries no divider (A0295,
  // Karan 25/08). Needs the second header line so a sentence saying "from:"
  // can never cut the message in half.
  /\n\s*From:\s*[^\n]{2,120}\n\s*(Sent|Date|To):\s/i,
];
// text/plain bodies arrive dressed in the artefacts of the HTML they were
// converted from: [image: mobilePhone] placeholders where the icons were,
// *asterisks* standing in for bold, and <angle-bracketed> URLs. None of it is
// the message (A0295, Karan 25/08).
function tidyPlain(text) {
  return String(text || '')
    .replace(/\r/g, '')
    .replace(/\[(?:image|cid)\s*:[^\]]*\]/gi, '')
    .replace(/<(https?:\/\/[^>\s]+)>/g, '$1')
    .replace(/\*([^*\n]{1,80})\*/g, '$1')
    .replace(/[ \t]+\n/g, '\n');
}
// Undo the sender's hard wrapping. A plain-text email breaks every line at
// ~70 characters; rendering each break as <br> reproduced the sender's column
// width instead of ours, which is why a wide window still showed a narrow,
// ragged block. A line is a continuation when the one before it ran long and
// the next isn't the start of something (a bullet, a header, a divider).
const UNWRAP_MIN = 55;
const startsBlock = (l) => !l.trim()
  || /^\s*([-*•>•]|\d+[.)])\s/.test(l)
  || /^\s*(From|To|Cc|Bcc|Sent|Date|Subject|Reply[- ]?To)\s*:/i.test(l)
  || /^\s*[-_=~]{3,}\s*$/.test(l);
function unwrapPlain(text) {
  const out = [];
  for (const line of String(text || '').split('\n')) {
    const prev = out.length ? out[out.length - 1] : null;
    if (prev !== null && !startsBlock(line) && !startsBlock(prev)
        && prev.trim().length >= UNWRAP_MIN && line.trim()) {
      out[out.length - 1] = prev.replace(/\s+$/, '') + ' ' + line.trim();
    } else {
      out.push(line);
    }
  }
  return out.join('\n');
}
const SIGNOFF_LINE = /\b(Kind regards|Best regards|Warm regards|Many thanks|Best wishes|Regards|Yours sincerely|Yours faithfully)\b/i;

function foldPlain(text) {
  // Drop a severed forwarded-header fragment BEFORE anything else — otherwise
  // the message opens on the tail of someone's To: line (A0298).
  const s = dropHeaderFragment(tidyPlain(text));

  // Boundaries come from mail-fold.js, which finds them WITH OR WITHOUT line
  // breaks. Anything filed before 27/08 has none of its own — intake collapsed
  // them — so keying on "\n" meant nothing folded and the whole mail, quoted
  // history and legal footer included, rendered as one wall (A0297, 28/08).
  const q = quoteCut(s);
  const head = (q > 0 ? s.slice(0, q) : s).trim();
  const quotedRaw = (q > 0 ? s.slice(q) : '').trim();
  let quoted = stripQuoteHeader(quotedRaw);

  const sc = signatureCut(head);
  let message = (sc > 0 ? head.slice(0, sc) : emailSplit(head).message).trim();
  const signature = (sc > 0 ? head.slice(sc) : emailSplit(head).signature).trim();

  // The sign-off gets its own paragraph; in a flat body it is welded to the
  // last sentence.
  const so = SIGNOFF_LINE.exec(message);
  if (so && so.index > 20) message = message.slice(0, so.index).trim() + '\n\n' + message.slice(so.index).trim();

  // The firm's legal block folds separately, inside the quote.
  let legal = '';
  const lc = quoted ? legalCut(quoted) : -1;
  if (lc > 0) { legal = quoted.slice(lc).trim(); quoted = quoted.slice(0, lc).trim(); }

  return {
    message: unwrapPlain(message).replace(/\n{3,}/g, '\n\n').trim(),
    signature,
    quoted: unwrapPlain(quoted).replace(/\n{3,}/g, '\n\n').trim(),
    legal: unwrapPlain(legal).replace(/\n{3,}/g, '\n\n').trim(),
    quotedAt: quotedSentAt(quotedRaw),
  };
}
// Escaped paragraphs at the reading measure (78ch, 15px/1.65 — C1 body spec).
// ---- "The questions are the work" (2a) --------------------------------------
// A plain-text list — asterisks, dashes, numbers or lettered points — is the only
// part of a mail anyone has to act on, and it reads as prose once the wrapping is
// gone. Pulled out onto paper grey with an orange dot each. Two items minimum: a
// single dashed line is a sentence, not a list.
const LIST_LINE = /^[ \t]*(?:[*\u2022\u00b7-]|\(?[a-z]\)|\(?\d{1,2}[.)])[ \t]+(.{8,})$/i;

function splitQuestions(message) {
  const lines = String(message || '').split('\n');
  const items = [], kept = [];
  let open = -1;                       // index in `items` still taking wrapped text
  for (const line of lines) {
    const m = LIST_LINE.exec(line);
    if (m) { items.push(m[1].trim()); open = items.length - 1; continue; }
    // unwrapPlain deliberately will not join a bullet to the line under it
    // (startsBlock treats "*"/"-"/"1." as a new block), so a wrapped question
    // arrives here as a loose line. It belongs to the item above, not the body.
    if (open >= 0 && line.trim()) { items[open] += ' ' + line.trim(); continue; }
    open = -1;
    kept.push(line);
  }
  if (items.length < 2) return { items: [], message };
  return { items, message: kept.join('\n') };
}

// ---- "Two honest gaps" (2a) -------------------------------------------------
// The sender says they attached something and nothing arrived. Stated, not left
// as an empty panel — and only ever when there is genuinely no attachment, so it
// can never contradict a file that is sitting right there.
const PROMISE = /((?:I\s+(?:enclose|have\s+attached|attach)|please\s+find\s+(?:attached|enclosed)|enclosed\s+(?:is|are|please\s+find)|attached\s+(?:is|are|hereto))[^.\n]{0,80})/i;

function enclosureGap(message, attachments) {
  if (!Array.isArray(attachments) || attachments.length) return null;
  const m = PROMISE.exec(String(message || ''));
  return m ? m[1].trim() : null;
}

// Artboard 2a sets the NEW message at 15px in the primary ink and the QUOTED
// history at 14.5px in the secondary — two different readings, and I had them
// the wrong way round until the conformance check caught it (Karan 28/08).
function paraHtml(t, { size = '15px', color = 'var(--ink)' } = {}) {
  const clean = escapeHtml(String(t || '').trim());
  if (!clean) return '';
  return clean.split(/\n{2,}/)
    .map((p) => `<p style="font-size:${size};line-height:1.65;color:${color};margin:0;max-width:72ch;text-wrap:pretty;">${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
}
const QUOTED_PARA = { size: '14.5px', color: 'var(--ink-2)' };

// ---- HTML-body sanitisation (house rule 0) ----------------------------------
// Same allowlist as views/mail-thread.js: scripts/forms/meta/base/dangerous URLs
// stripped, links forced target=_blank noopener. No DOMPurify → no HTML, ever.
let purifyHooked = false;
function sanitizeEmailHtml(html) {
  const DP = (typeof window !== 'undefined' && window.DOMPurify) || (typeof DOMPurify !== 'undefined' ? DOMPurify : null);
  if (!DP || !DP.isSupported) return null;
  if (!purifyHooked) {
    purifyHooked = true;
    DP.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') { node.setAttribute('target', '_blank'); node.setAttribute('rel', 'noopener noreferrer'); }
    });
  }
  return DP.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'meta', 'base', 'link', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['formaction'],
  });
}
const EMAIL_STYLE = "<style>body{font:15px/1.6 -apple-system,'Helvetica Neue',Arial,sans-serif;color:#0C1139;margin:2px 0;word-wrap:break-word;overflow-wrap:break-word}"
  + 'img{max-width:100%;height:auto}table{max-width:100%}'
  + 'blockquote{border-left:3px solid #DEDEDA;margin:8px 0;padding-left:12px;color:#55564F}a{color:#0A40C0}</style>';

// ---- view-message normalisation ---------------------------------------------
// Two sources, one shape:
//  • a live Gmail thread message (google-api getThreadFull): { from:{name,email},
//    to, date, subject, text, html, attachments:[{filename,size,…}] }
//  • a stored entry (projectEmails row via normArchiver, or an enquiry's stored
//    email): rfc822 `from` string, plain-text body only.
function vmFromGmail(m) {
  return {
    name: (m.from && (m.from.name || m.from.email)) || '—',
    email: (m.from && m.from.email) || '',
    to: m.to || '',
    cc: m.cc || '',
    date: (m.date instanceof Date && !isNaN(m.date)) ? m.date : toDate(m.date),
    subject: m.subject || '',
    text: (m.text && m.text.trim()) ? m.text : '',
    html: m.html || null,
    attachments: Array.isArray(m.attachments) ? m.attachments : [],
  };
}
function vmFromEntry(e) {
  return {
    name: e.senderName || parseName(e.from),
    email: e.email || extractEmail(e.from),
    to: e.to || '',
    cc: e.cc || '',
    date: toDate(e.date),
    subject: e.subject || '',
    text: e.body || e.snippet || '',
    html: null,
    // null = NOT KNOWN (a stored copy that never recorded its attachments), as
    // opposed to [] = known to have none. Only the second may say "promised
    // and never arrived" (A0333, 09/10: four PDFs were there; the copy was
    // read from track@'s stored entry, which kept no list).
    attachments: Array.isArray(e.attachments) ? e.attachments : null,
  };
}

// =============================================================================
// THE READER (artboard 1a) — a 980px card on a dim backdrop.
// openMailReader({ session, entry, messages, load, job, jobKind, project })
//  • entry    — a stored single message (enquiry email / projectEmails row).
//  • messages — a full Gmail thread (chronological) → real thread strip + chips.
//  • load     — async () => messages, for callers that fetch after opening.
//  • job      — 'A0274' / '7014'; jobKind — 'Enquiry' | 'Project'.
// =============================================================================
// ---- the ··· menu (artboard 2b) ---------------------------------------------
// 320px, five groups, in the artboard's order. What it deliberately leaves out
// is as much a part of the design as what it has: no archive and no folders (an
// email in Track is already filed against a job, so the only move is to another
// job), no "open in Gmail" per the Files/Mail rule, and no spam or block —
// mail is filtered before Track ever sees it.
//
// An item with nothing behind it renders DISABLED with the reason on hover
// rather than as a live control that quietly does nothing.
const MENU_ROW = 'width:100%;text-align:left;border:0;background:none;font-family:var(--font-sans);'
  + 'font-size:13.5px;padding:9px 16px;display:flex;align-items:center;gap:10px;';

function menuGroup(label, rows) {
  const live = rows.filter(Boolean);
  if (!live.length) return '';
  return `<div style="padding:12px 16px 4px;font-size:9.5px;font-weight:500;letter-spacing:0.14em;`
    + `text-transform:uppercase;color:var(--ink-3);">${escapeHtml(label)}</div>${live.join('')}`;
}

function menuItem(id, text, { note = '', why = '' } = {}) {
  const off = !!why;
  return `<button type="button" ${off ? 'disabled' : ''} ${id ? `id="${id}"` : ''}
    title="${escapeHtml(why || '')}"
    style="${MENU_ROW}color:${off ? 'var(--ink-3)' : 'var(--ink)'};cursor:${off ? 'default' : 'pointer'};">
    <span style="flex:1;min-width:0;">${escapeHtml(text)}</span>
    ${note ? `<span style="${MONO}font-size:11.5px;color:var(--ink-3);">${escapeHtml(note)}</span>` : ''}
  </button>`;
}

export function openMailReader({ session = null, entry = null, messages = null, load = null, job = '', jobKind = 'Project', project = null,
  responseBy = null, onSetResponseBy = null } = {}) {
  const overlay = document.createElement('div');
  overlay.setAttribute('style', BACKDROP);
  overlay.className = 'mr-backdrop';
  document.body.appendChild(overlay);
  const close = () => { overlay.remove(); document.removeEventListener('keydown', onKey, true); };
  // Keyboard, once the reader is open (artboard 2b). Ignored while the caret is
  // in a field, so typing a reply never triggers one.
  function onKey(e) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    const hit = (sel) => { const el = document.querySelector(sel); if (el) { e.preventDefault(); el.click(); } };
    switch (e.key.toLowerCase()) {
      case 'j': hit('#mr-next'); break;
      case 'k': hit('#mr-prev'); break;
      case 'r': hit('#mr-reply'); break;
      case 'a': hit('#mr-replyall'); break;
      case 'f': hit('#mr-forward'); break;
      case 'q': hit('#mr-quote-toggle'); break;
      case 'u': e.preventDefault(); close(); break;   // no read state to write yet
      default: break;
    }
  }
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  const CARD = 'width:1120px;max-width:100%;background:var(--surface);border-radius:20px;box-shadow:0 16px 56px rgba(12,17,57,0.20);overflow:hidden;';
const CARD_CLASS = 'mr-card';

  let vms = Array.isArray(messages) && messages.length ? messages.map(vmFromGmail)
    : entry ? [vmFromEntry(entry)] : [];
  let rawThread = Array.isArray(messages) && messages.length ? messages : null; // for compose quoting
  let current = vms.length - 1; // newest

  if (load) {
    overlay.innerHTML = `<div class="${CARD_CLASS}" style="${CARD}"><div style="padding:40px 30px;color:var(--ink-3);font-size:13px;">Loading the thread…</div></div>`;
    Promise.resolve().then(load).then((msgs) => {
      if (!document.body.contains(overlay)) return; // closed while loading
      if (Array.isArray(msgs) && msgs.length) {
        vms = msgs.map(vmFromGmail);
        rawThread = msgs;
        current = vms.length - 1;
      }
      paint();
    }).catch((err) => {
      if (!document.body.contains(overlay)) return;
      if (vms.length) { paint(); return; }
      overlay.innerHTML = `<div class="${CARD_CLASS}" style="${CARD}"><div style="padding:40px 30px;display:flex;flex-direction:column;gap:12px;">
        <span style="${EYEBROW}color:var(--brand-orange);">Couldn't open this email</span>
        <span style="font-size:14px;color:var(--ink-2);line-height:1.6;">${escapeHtml((err && err.message) || 'error')}</span>
        <div><span id="mr-close-err" style="border:1px solid var(--hair);border-radius:8px;padding:10px 16px;font-size:13px;cursor:pointer;display:inline-block;">Close</span></div>
      </div></div>`;
      overlay.querySelector('#mr-close-err').addEventListener('click', close);
    });
  } else {
    paint();
  }

  function paint() {
    if (!vms.length) {
      overlay.innerHTML = `<div class="${CARD_CLASS}" style="${CARD}"><div style="padding:40px 30px;color:var(--ink-3);font-size:13px;">Nothing to read — no message stored.</div></div>`;
      overlay.addEventListener('click', close, { once: true });
      return;
    }
    const vm = vms[current];
    const count = vms.length > 1 ? vms.length : Math.max(1, Number(entry && entry.msgCount) || 1);
    const rawSubject = vm.subject || (entry && entry.subject) || '';
    const subject = bareSubject(rawSubject) || '(no subject)';
    const forwarded = isFwdSubject(rawSubject);
    const dir = (entry && entry.dir) || dirOf(vm.email || vm.name, null);
    const received = dir === 'Received';
    // Cc'd people count as recipients too (Karan 13/10: Mail showed only To) —
    // listed after the To people, marked, and included in Reply all.
    const recipients = [...parseRecipients(vm.to), ...parseRecipients(vm.cc).map((r) => ({ ...r, cc: true }))];
    const firstRcpt = recipients[0];
    const others = recipients.slice(1);

    // ---- body parts (plain text) or sanitised HTML iframe -------------------
    let bodyBlock, sigBlock = '', quotedBlock = '';
    let iframeDoc = null;
    let askBlock = '', gapBlock = '', plainMessage = '', askItems = [], quotedTail = '', useQuoteForAsk = false, fullQuoted = '';
    if (vm.text) {
      const parts = foldPlain(vm.text);
      // The questions live in whichever part actually asked them — on a
      // forward that is the QUOTED original, not the covering note (A0297).
      const fromQuote = splitPoints(parts.quoted);
      const fromMsg = splitPoints(parts.message);
      useQuoteForAsk = fromQuote.items.length >= 2 && fromQuote.items.length >= fromMsg.items.length;
      const picked = useQuoteForAsk ? fromQuote : fromMsg;
      askItems = picked.items;
      const message = useQuoteForAsk ? parts.message : picked.before;
      if (useQuoteForAsk) { parts.quoted = picked.before; quotedTail = picked.after; }
      bodyBlock = message.trim()
        ? `<div style="display:flex;flex-direction:column;gap:15px;max-width:74ch;">${paraHtml(message)}</div>`
        : '<div style="font-size:14px;color:var(--ink-3);">(no body stored)</div>';
      if (askItems.length) {
        askBlock = `
          <div style="background:var(--brief-bg);border-radius:12px;padding:18px 20px;display:flex;flex-direction:column;gap:14px;">
            <div style="${EYEBROW}color:var(--brand-orange);">${askItems.length === 1 ? 'One question asked' : `${escapeHtml(numWord(askItems.length))} question${askItems.length === 1 ? '' : 's'} asked`}</div>
            ${askItems.map((q) => `<div style="display:flex;align-items:flex-start;gap:12px;">
              <span style="width:6px;height:6px;border-radius:50%;background:var(--brand-orange);flex:none;margin-top:8px;"></span>
              <p style="font-size:14.5px;line-height:1.6;color:var(--ink);margin:0;max-width:66ch;text-wrap:pretty;">${escapeHtml(q)}</p>
            </div>`).join('')}
          </div>`;
      }
      plainMessage = parts.message;
      fullQuoted = [parts.quoted, quotedTail].filter(Boolean).join(' ');
      if (parts.signature) {
        const domain = vm.email && vm.email.includes('@') ? vm.email.split('@')[1] : '';
        sigBlock = `
          <div style="display:flex;align-items:center;gap:10px;">
            <span id="mr-sig-toggle" style="font-size:12.5px;color:var(--link);cursor:pointer;">Show signature</span>
            ${domain ? `<span style="font-size:12px;color:var(--ink-3);">${escapeHtml(domain)}</span>` : ''}
          </div>
          <div id="mr-sig" style="display:none;flex-direction:column;gap:12px;max-width:78ch;border-left:2px solid var(--hair);padding-left:16px;">${paraHtml(parts.signature, QUOTED_PARA)}</div>`;
      }
      if (parts.quoted) {
        // "Quoted history, boxed" (2a): everything after the break sits behind a
        // grey rule inside its own box, collapsed until asked for.
        quotedBlock = `
          <div style="border:1px solid var(--hair);border-radius:14px;overflow:hidden;">
            <div id="mr-quote-toggle" style="padding:13px 18px;display:flex;align-items:center;gap:10px;cursor:pointer;">
              <span style="${EYEBROW}color:var(--ink-3);flex:1;min-width:0;">Quoted &mdash; ${escapeHtml(quotedNote(parts.quoted, vm, parts.quotedAt))}</span>
              <span id="mr-quote-word" style="font-size:12.5px;color:var(--link);">Collapse</span>
            </div>
            <div id="mr-quoted" style="display:flex;flex-direction:column;gap:15px;padding:20px 18px 22px 22px;border-left:2px solid var(--hair-strong);margin:0;">
              ${paraHtml(parts.quoted, QUOTED_PARA)}
              ${useQuoteForAsk ? askBlock : ''}
              ${quotedTail ? paraHtml(quotedTail, QUOTED_PARA) : ''}
              ${parts.legal ? `<div style="display:flex;align-items:center;gap:10px;">
                <span id="mr-legal-toggle" style="font-size:12.5px;color:var(--link);cursor:pointer;">Show legal footer</span>
                <span style="font-size:12px;color:var(--ink-3);">${escapeHtml(footerNote(parts.legal))}</span>
              </div>
              <div id="mr-legal" style="display:none;flex-direction:column;gap:12px;">${paraHtml(parts.legal, QUOTED_PARA)}</div>` : ''}
            </div>
          </div>`;
      }
    } else if (vm.html) {
      const clean = sanitizeEmailHtml(vm.html);
      if (clean != null) {
        iframeDoc = EMAIL_STYLE + clean;
        bodyBlock = '<iframe id="mr-frame" sandbox="allow-same-origin allow-popups" style="width:100%;min-height:60px;border:0;display:block;"></iframe>';
      } else {
        bodyBlock = '<div style="font-size:14px;color:var(--ink-3);">(HTML body hidden — sanitiser unavailable)</div>';
      }
    } else {
      bodyBlock = '<div style="font-size:14px;color:var(--ink-3);">The full email isn’t indexed yet — it appears here after the next archiver sweep.</div>';
    }

    // ---- attachments — ONLY from real data (never fabricated) ---------------
    const atts = vm.attachments || [];
    const attRows = atts.map((a) => {
      const name = a.filename || a.name || 'attachment';
      const ext = (String(name).match(/\.(\w{1,5})$/) || [, 'FILE'])[1].toUpperCase().slice(0, 4);
      const size = fmtSize(a.size);
      const state = a.docNumber
        ? `<span style="${MONO}">${escapeHtml(a.docNumber)}</span> · filed`
        : (size || '');
      return `
        <div style="display:flex;align-items:center;gap:14px;border:1px solid var(--hair);border-radius:12px;padding:14px 16px;">
          <span style="width:34px;height:42px;border:1px solid var(--hair-strong);border-radius:3px;display:flex;align-items:center;justify-content:center;${MONO}font-size:9px;color:var(--ink-3);flex:none;">${escapeHtml(ext)}</span>
          <div style="display:flex;flex-direction:column;gap:3px;min-width:0;">
            <span style="font-size:14px;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(name)}</span>
            <span style="${MONO}font-size:11.5px;color:var(--ink-3);">${state}</span>
          </div>
        </div>`;
    }).join('');
    // The deadline the sender stated. Read once, up here, because both the reply
    // openers and the side column's card need it.
    const found = findDeadline(plainMessage, vm.date);
    const already = responseBy ? new Date(responseBy) : null;

    // "Two honest gaps" (2a) — only once the attachment list is actually known,
    // so the card can never contradict a file sitting right there.
    // The promise usually sits in the ORIGINAL request, not the covering note.
    const promised = enclosureGap(plainMessage + ' ' + fullQuoted, vm.attachments);
    if (promised) {
      gapBlock = `
        <div style="border:1px dashed var(--brand-orange);border-radius:14px;padding:16px 18px;display:flex;align-items:center;gap:16px;">
          <span style="width:34px;height:42px;border:1px dashed var(--brand-orange);border-radius:3px;display:flex;align-items:center;justify-content:center;${MONO}font-size:9px;color:var(--brand-orange);flex:none;">?</span>
          <div style="display:flex;flex-direction:column;gap:3px;min-width:0;">
            <span style="font-size:14px;color:var(--ink);">An enclosure was promised and never arrived</span>
            <span style="font-size:12.5px;line-height:1.5;color:var(--ink-2);">&ldquo;${escapeHtml(promised)}&rdquo; &mdash; no attachment reached Track with this email.</span>
          </div>
        </div>`;
    }

    // "Start a reply" (2a): the openers this message actually invites. Each one
    // opens the compose window already addressed and filed — none of them sends
    // anything, and none is offered unless the message earns it.
    const openers = [];
    if (found) openers.push(['Acknowledge and give a date', `Thank you for your email. We will come back to you by ${longDay(found.date)}.`]);
    if (askItems.length) openers.push([`Answer the ${askItems.length === 1 ? 'question' : `${numWord(askItems.length).toLowerCase()} questions`}`, askItems.map((q, i) => `${i + 1}. ${q}\n\n`).join('')]);
    if (promised) openers.push(['Ask for the enclosure', 'Thank you for your email. The attachment you refer to did not come through — could you resend it?']);
    openers.push(['Acknowledge', 'Thank you for your email.']);
    const replyBlock = openers.length ? `
      <div style="border-top:1px solid var(--hair);padding-top:20px;display:flex;flex-direction:column;gap:12px;">
        <div style="${EYEBROW}color:var(--ink-3);">Start a reply</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;">
          ${openers.map(([label], i) => `<button type="button" class="mr-opener" data-i="${i}" style="min-height:40px;padding:0 14px;border-radius:8px;border:1px solid var(--hair);background:var(--surface);font-family:var(--font-sans);font-size:13px;color:var(--ink);cursor:pointer;">${escapeHtml(label)}</button>`).join('')}
        </div>
      </div>` : '';

    const attBlock = atts.length ? `
      <div style="border-top:1px solid var(--hair);padding-top:20px;display:flex;flex-direction:column;gap:12px;">
        <div style="${EYEBROW}color:var(--ink-3);">Attachment${atts.length === 1 ? '' : 's'}</div>
        ${attRows}
      </div>` : '';

    // ---- thread strip (one chip per message, current filled) ----------------
    let strip = '';
    if (vms.length > 1) {
      strip = `
      <div style="padding:14px 30px;background:var(--brief-bg);border-bottom:1px solid var(--hair);display:flex;align-items:center;gap:0;overflow-x:auto;">
        ${vms.map((m, i) => {
          const on = i === current;
          const chip = `
          <div class="mr-chip" data-i="${i}" style="display:flex;align-items:center;gap:10px;padding:7px 12px;border-radius:8px;cursor:pointer;flex:none;${on ? 'background:var(--surface);border:1px solid var(--hair);' : ''}">
            <span style="width:7px;height:7px;border-radius:50%;background:${on ? 'var(--brand-navy)' : 'var(--hair-strong)'};"></span>
            <span style="font-size:12.5px;color:${on ? 'var(--ink)' : 'var(--ink-3)'};white-space:nowrap;">${escapeHtml(m.name)}</span>
            <span style="${MONO}font-size:11.5px;color:var(--ink-3);">${escapeHtml(ddmm(m.date))}</span>
          </div>`;
          return i ? `<span style="width:22px;height:1px;background:var(--hair);flex:none;"></span>${chip}` : chip;
        }).join('')}
      </div>`;
    }

    // ---- header meta line ----------------------------------------------------
    const jobLabel = job ? `${jobKind} ${job}` : '';
    const metaBits = [`${count} message${count === 1 ? '' : 's'}`];
    if (forwarded) metaBits.push('forwarded');
    if (count > 1 && vms.length === 1) metaBits.push('latest shown');

    // ---- side column -----------------------------------------------------
    // Recipients as names; what it is filed against. Deliberately absent: the
    // "Track found a date" suggestion (no date parsing exists in the app yet —
    // a date must be OFFERED, never applied, so nothing is faked) and "Move to
    // another job" (no move flow is wired from these surfaces yet).
    const rcptCol = recipients.length
      ? recipients.map((r, i) => `<span style="font-size:13px;color:${i ? 'var(--ink-2)' : 'var(--ink)'};" title="${escapeHtml(r.email)}">${escapeHtml(r.name)}</span>`).join('')
      : '<span style="font-size:13px;line-height:1.5;color:var(--ink-3);">Not stored for this email. Anything sent from here goes to the sender address only.</span>';
    // ---- "Track read a deadline" -----------------------------------------
    // Offered, never applied. Without a handler from the caller the card still
    // states what Track read, it just cannot write it.
    const deadlineCol = (found || (already && !isNaN(already))) ? `
      <div style="display:flex;flex-direction:column;gap:10px;">
        <div style="${EYEBROW}color:var(--brand-orange);">${already && !isNaN(already) ? 'Response-by date' : 'Track read a deadline'}</div>
        ${already && !isNaN(already)
          ? `<p style="font-size:13.5px;line-height:1.55;color:var(--ink);margin:0;">${escapeHtml(longDay(already))}</p>`
          : `<p style="font-size:13.5px;line-height:1.55;color:var(--ink-2);margin:0;">&ldquo;${escapeHtml(found.phrase)}&rdquo; from ${escapeHtml(ddmm(vm.date))} &mdash; <strong style="font-weight:500;color:var(--ink);">${escapeHtml(longDay(found.date))}</strong>.</p>
             ${onSetResponseBy ? '<button type="button" id="mr-setby" class="mr-offer">Set as the response-by date</button>' : ''}`}
      </div>
      <div style="height:1px;background:var(--hair);"></div>` : '';

    const filedCol = job ? `
      <div style="display:flex;flex-direction:column;gap:11px;">
        <div style="${EYEBROW}color:var(--ink-3);">Filed against</div>
        <span style="font-size:13.5px;color:var(--ink);">${escapeHtml(jobLabel)}</span>
      </div>` : '';

    // ---- the card --------------------------------------------------------
    overlay.innerHTML = `
    <div class="${CARD_CLASS}" style="${CARD}">
      <div style="padding:20px 28px 18px;border-bottom:1px solid var(--hair);display:flex;align-items:flex-start;gap:24px;">
        <div style="display:flex;flex-direction:column;gap:6px;min-width:0;">
          <div style="font-size:19px;font-weight:500;letter-spacing:-0.02em;line-height:1.3;color:var(--ink);max-width:62ch;">${escapeHtml(subjectRefs(subject).rest)}</div>
          ${(() => { const sr = subjectRefs(subject); return (sr.refs.length || sr.trimmed) ? `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">${sr.refs.map((r) => `<span style="${MONO}font-size:11.5px;color:var(--ink-2);border:1px solid var(--hair);border-radius:6px;padding:3px 8px;">${escapeHtml(r)}</span>`).join('')}${sr.trimmed ? `<span title="${escapeHtml(sr.full)}" style="font-size:12.5px;color:var(--link);cursor:help;">Full subject line</span>` : ''}</div>` : ''; })()}
          <div style="display:flex;align-items:center;gap:10px;">
            ${jobLabel ? `<span style="${EYEBROW}color:var(--ink-3);">${escapeHtml(jobLabel)}</span>
            <span style="width:3px;height:3px;border-radius:50%;background:var(--hair-strong);"></span>` : ''}
            <span style="font-size:12.5px;color:var(--ink-3);">${escapeHtml(metaBits.join(' · '))}</span>
          </div>
        </div>
        <div style="margin-left:auto;display:flex;align-items:center;gap:10px;">
          <span style="${MONO}font-size:12px;color:var(--ink-3);white-space:nowrap;">${escapeHtml(stamp(vm.date))}</span>
          ${vms.length > 1 ? `
            <span id="mr-prev" role="button" tabindex="0" aria-label="Previous message" style="${SQ}${current > 0 ? 'color:var(--ink);cursor:pointer;' : 'color:var(--ink-3);'}">&lsaquo;</span>
            <span style="${MONO}font-size:11.5px;color:var(--ink-2);white-space:nowrap;">${current + 1} / ${vms.length}</span>
            <span id="mr-next" role="button" tabindex="0" aria-label="Next message" style="${SQ}${current < vms.length - 1 ? 'color:var(--ink);cursor:pointer;' : 'color:var(--ink-3);'}">&rsaquo;</span>` : ''}
          <span id="mr-menu-btn" role="button" tabindex="0" aria-label="Options" style="${SQ}color:var(--ink-2);cursor:pointer;">&#183;&#183;&#183;</span>
          <span id="mr-close" role="button" tabindex="0" aria-label="Close" style="${SQ}color:var(--ink-2);cursor:pointer;">×</span>
        </div>
      </div>

      ${strip}

      <div style="display:grid;grid-template-columns:1fr 300px;" class="mr-grid">
        <div style="padding:24px 30px 26px;display:flex;flex-direction:column;gap:20px;min-width:0;">
          <div style="display:flex;align-items:center;gap:13px;">
            <span style="width:36px;height:36px;border-radius:50%;background:var(--ink-fill);color:var(--ink-fill-ink);display:flex;align-items:center;justify-content:center;font-size:12.5px;font-weight:500;flex:none;">${escapeHtml(initialsOf(vm.name))}</span>
            <div style="display:flex;flex-direction:column;gap:2px;min-width:0;">
              <div style="display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;">
                <span style="font-size:15px;font-weight:500;color:var(--ink);">${escapeHtml(vm.name)}</span>
                ${vm.email && vm.email.toLowerCase() !== vm.name.toLowerCase() ? `<span style="${MONO}font-size:12px;color:var(--ink-3);">${escapeHtml(vm.email)}</span>` : ''}
              </div>
              ${firstRcpt ? `<div style="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;">
                <span style="font-size:12.5px;color:var(--ink-3);">to ${escapeHtml(firstRcpt.name)}</span>
                ${others.length ? `<span id="mr-others-toggle" style="font-size:12.5px;color:var(--link);cursor:pointer;">and ${others.length} other${others.length === 1 ? '' : 's'}</span>` : ''}
              </div>
              ${others.length ? `<div id="mr-others" style="display:none;flex-direction:column;gap:3px;padding-top:4px;">${others.map((r) => `<span style="font-size:12.5px;color:var(--ink-2);" title="${escapeHtml(r.email)}">${escapeHtml(r.name)}${r.cc ? ' (cc)' : ''}</span>`).join('')}</div>` : ''}` : ''}
            </div>
          </div>

          ${bodyBlock}
          ${useQuoteForAsk ? '' : askBlock}
          ${sigBlock}
          ${quotedBlock}
          ${gapBlock}
          ${attBlock}
          ${replyBlock}
        </div>

        <div style="border-left:1px solid var(--hair);background:var(--brief-bg);padding:24px 22px;display:flex;flex-direction:column;gap:22px;">
          ${deadlineCol}
          ${filedCol}
          <div style="height:1px;background:var(--hair);"></div>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <div style="${EYEBROW}color:var(--ink-3);">Sender</div>
            <span style="font-size:13px;color:var(--ink);">${escapeHtml(vm.name || '—')}</span>
            ${vm.email ? `<span style="${MONO}font-size:11.5px;color:var(--ink-3);">${escapeHtml(vm.email)}</span>` : ''}
          </div>
          <div style="height:1px;background:var(--hair);"></div>
          <div style="display:flex;flex-direction:column;gap:11px;">
            <div style="${EYEBROW}color:var(--ink-3);">Recipients</div>
            <div style="display:flex;flex-direction:column;gap:7px;">${rcptCol}</div>
          </div>
        </div>
      </div>

      <div style="padding:18px 30px;border-top:1px solid var(--hair);background:var(--brief-bg);display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <span style="font-size:12.5px;color:var(--ink-3);">${escapeHtml(received ? 'Received' : 'Sent')} ${escapeHtml(stamp(vm.date))}${job ? ` · filed against <span style="${MONO}">${escapeHtml(job)}</span>`.replace(/</g, '').replace(/span/g, '<span').replace(/\/span/g, '</span') : ''}</span>
        <div style="margin-left:auto;display:flex;gap:12px;">
          <button id="mr-forward" style="min-height:44px;padding:0 18px;border-radius:8px;border:1px solid var(--hair);background:transparent;font-family:inherit;font-size:13.5px;color:var(--ink);cursor:pointer;">Forward</button>
          <button id="mr-replyall" style="min-height:44px;padding:0 18px;border-radius:8px;border:1px solid var(--hair);background:transparent;font-family:inherit;font-size:13.5px;color:var(--ink);cursor:pointer;">Reply all</button>
          <button id="mr-reply" style="min-height:44px;padding:0 22px;border-radius:8px;border:0;background:var(--brand-blue);font-family:inherit;font-size:13.5px;font-weight:500;color:var(--brand-white);cursor:pointer;">Reply</button>
        </div>
      </div>
    </div>`;

    // ---- wiring ------------------------------------------------------------
    overlay.querySelector('#mr-close').addEventListener('click', close);

    // ---- the ··· menu (2b) ----------------------------------------------
    const menuBtn = overlay.querySelector('#mr-menu-btn');
    if (menuBtn) menuBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const open = overlay.querySelector('#mr-menu');
      if (open) { open.remove(); return; }
      const sender = vm.name || vm.email || 'this sender';
      const menu = document.createElement('div');
      menu.id = 'mr-menu';
      menu.setAttribute('style', 'position:absolute;z-index:5;width:320px;background:var(--surface);'
        + 'border:1px solid var(--hair);border-radius:14px;box-shadow:0 16px 40px rgba(12,17,57,0.18);'
        + 'padding:6px 0 10px;overflow:hidden;');
      menu.innerHTML =
        menuGroup('This email', [
          menuItem('', 'Mark as unread', { why: 'Track does not keep a read state for an email yet.' }),
          menuItem('', 'Flag for follow-up', { why: 'Nowhere to store a flag against an email yet.' }),
          menuItem('mr-m-print', 'Print'),
          menuItem('', 'Download original', { note: '.eml', why: 'The raw message is not stored — only the text Track filed.' }),
          menuItem('mr-m-link', 'Copy link to this email'),
          menuItem('mr-m-headers', 'Show original headers'),
        ]) +
        menuGroup('Filing', [
          menuItem('', 'Move to another project…', { why: 'No move flow is wired from this surface yet.' }),
          menuItem('', 'Also file against a project…', { why: 'No move flow is wired from this surface yet.' }),
        ]) +
        menuGroup('File attachments', [
          atts.length
            ? menuItem('', `File ${atts.length} attachment${atts.length === 1 ? '' : 's'}…`, { why: 'Filing an attachment from here is not wired yet.' })
            : menuItem('', 'File attachments', { note: 'none on this email', why: 'This email carries no attachment.' }),
        ]) +
        menuGroup('Follow-up', [
          found && onSetResponseBy
            ? menuItem('mr-m-setby', 'Set response-by date', { note: ddmm(found.date) })
            : menuItem('', 'Set response-by date', { why: found ? 'This surface cannot write the date.' : 'No deadline was found in this message.' }),
          menuItem('', 'Add to the calendar', { why: 'Not wired from the reader yet.' }),
          menuItem('', 'Raise an instruction from this', { why: 'Not wired from the reader yet.' }),
          menuItem('', 'Assign to someone…', { why: 'An email has no owner field yet.' }),
        ]) +
        menuGroup('Sender', [
          menuItem('', `All email from ${sender}`, { why: 'No per-sender view exists yet.' }),
          menuItem('', 'Add to the client record', { why: 'Not wired from the reader yet.' }),
          menuItem('', 'Stop filing from this address', { why: 'Filing rules are set at intake, not here.' }),
        ]);
      menuBtn.parentElement.style.position = 'relative';
      menu.style.top = '44px';
      menu.style.right = '0';
      menuBtn.parentElement.appendChild(menu);

      const shut = () => { const m = overlay.querySelector('#mr-menu'); if (m) m.remove(); };
      setTimeout(() => document.addEventListener('click', shut, { once: true }), 0);
      menu.addEventListener('click', (e) => e.stopPropagation());

      const on = (id, fn) => { const b = menu.querySelector(id); if (b) b.addEventListener('click', () => { fn(); shut(); }); };
      on('#mr-m-print', () => window.print());
      on('#mr-m-link', () => {
        const url = job ? `${location.origin}/${jobKind === 'Enquiry' ? 'enquiries' : 'projects'}/${encodeURIComponent(job)}` : location.href;
        (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).catch(() => {});
      });
      on('#mr-m-headers', () => {
        const hdrs = [['From', vm.name ? `${vm.name} <${vm.email || ''}>` : (vm.email || '—')],
          ['To', vm.to || '(not stored)'], ['Date', stamp(vm.date) || '—'],
          ['Subject', vm.subject || (entry && entry.subject) || '—']];
        const box = document.createElement('div');
        box.setAttribute('style', 'position:fixed;inset:0;z-index:300;background:rgba(var(--scrim-rgb), 0.34);display:flex;align-items:center;justify-content:center;padding:40px;');
        box.innerHTML = `<div style="background:var(--surface);border-radius:16px;padding:22px 26px;max-width:640px;width:100%;">
          <div style="${EYEBROW}color:var(--ink-3);margin-bottom:14px;">Original headers &mdash; as Track stored them</div>
          ${hdrs.map(([k, v]) => `<div style="display:grid;grid-template-columns:88px 1fr;gap:12px;padding:7px 0;border-bottom:1px solid var(--hair-soft);">
            <span style="${EYEBROW}color:var(--ink-3);">${escapeHtml(k)}</span>
            <span style="${MONO}font-size:12px;color:var(--ink);word-break:break-word;">${escapeHtml(String(v))}</span></div>`).join('')}
          <div style="font-size:12px;color:var(--ink-3);margin-top:14px;line-height:1.5;">Track files the text of a message, not its raw source, so this is what it holds rather than the full RFC822 header block.</div>
        </div>`;
        box.addEventListener('click', () => box.remove());
        document.body.appendChild(box);
      });
      on('#mr-m-setby', () => { const b = overlay.querySelector('#mr-setby'); if (b) b.click(); });
    });

    // message stepper
    const step = (id, to) => {
      const el = overlay.querySelector(id);
      if (el) el.addEventListener('click', () => { if (to >= 0 && to < vms.length) { current = to; paint(); } });
    };
    step('#mr-prev', current - 1);
    step('#mr-next', current + 1);

    // The ONLY thing that writes the date. Track reads it; a person sets it.
    const setBy = overlay.querySelector('#mr-setby');
    if (setBy && onSetResponseBy && found) {
      setBy.addEventListener('click', async () => {
        setBy.disabled = true;
        const was = setBy.textContent;
        setBy.textContent = 'Setting…';
        try {
          await onSetResponseBy(found.date);
          responseBy = found.date.toISOString();
          paint();                                  // the card now reads it back
        } catch (e) {
          setBy.disabled = false;
          setBy.textContent = was;
          alert(e && e.message ? e.message : String(e));
        }
      });
    }
    const toggle = (btnId, elId, showLabel, hideLabel) => {
      const btn = overlay.querySelector(btnId);
      const el = overlay.querySelector(elId);
      if (!btn || !el) return;
      btn.addEventListener('click', () => {
        const open = el.style.display !== 'none';
        el.style.display = open ? 'none' : 'flex';
        btn.textContent = open ? showLabel : hideLabel;
      });
    };
    toggle('#mr-sig-toggle', '#mr-sig', 'Show signature', 'Hide signature');
    const qBtn = overlay.querySelector('#mr-quote-toggle');
    const qBox = overlay.querySelector('#mr-quoted');
    const qWord = overlay.querySelector('#mr-quote-word');
    const lBtn = overlay.querySelector('#mr-legal-toggle');
    const lBox = overlay.querySelector('#mr-legal');
    if (lBtn && lBox) lBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const open = lBox.style.display !== 'none';
      lBox.style.display = open ? 'none' : 'flex';
      lBtn.textContent = open ? 'Show legal footer' : 'Hide legal footer';
    });
    if (qBtn && qBox) qBtn.addEventListener('click', () => {
      const open = qBox.style.display !== 'none';
      qBox.style.display = open ? 'none' : 'flex';
      if (qWord) qWord.textContent = open ? 'Show' : 'Collapse';
    });
    toggle('#mr-others-toggle', '#mr-others', `and ${others.length} other${others.length === 1 ? '' : 's'}`, 'show fewer');

    overlay.querySelectorAll('.mr-chip').forEach((el) => el.addEventListener('click', () => {
      current = +el.dataset.i;
      paint();
    }));

    // Sandboxed HTML body: srcdoc set post-mount, height auto-fitted, no scripts.
    const frame = overlay.querySelector('#mr-frame');
    if (frame && iframeDoc != null) {
      const fit = () => {
        try {
          const b = frame.contentDocument && frame.contentDocument.body;
          if (b && b.scrollHeight) frame.style.height = Math.min(b.scrollHeight + 24, 1600) + 'px';
        } catch (_) { /* sandbox */ }
      };
      frame.addEventListener('load', () => {
        fit();
        try { frame.contentDocument.querySelectorAll('img').forEach((im) => im.addEventListener('load', fit)); } catch (_) { /* ignore */ }
      });
      frame.srcdoc = iframeDoc;
    }

    // Reply / Reply all / Forward → mail-compose, pre-filled with the job.
    // Reply-all recipients: the sender plus everyone on To, minus the viewer.
    const me = String((session && session.email) || '').toLowerCase();
    const allTo = [vm.email, ...recipients.map((r) => r.email)]
      .filter(Boolean)
      .filter((a, i, arr) => arr.indexOf(a) === i)
      .filter((a) => a.toLowerCase() !== me);
    const composeOpts = { job: job || '', project, subject: rawSubject };
    const openCompose = (extra) => renderCompose(session, { ...composeOpts, thread: rawThread || undefined, ...extra });
    overlay.querySelector('#mr-reply').addEventListener('click', () => openCompose({ mode: 'reply', to: vm.email || '' }));
    overlay.querySelector('#mr-replyall').addEventListener('click', () => openCompose({ mode: 'reply', to: allTo.join(', ') }));
    overlay.querySelector('#mr-forward').addEventListener('click', () => openCompose({ mode: 'forward' }));

    // An opener drops you into the same compose window with the first words
    // already there. It writes a draft, never a sent message.
    overlay.querySelectorAll('.mr-opener').forEach((b) => b.addEventListener('click', () => {
      const pair = openers[Number(b.dataset.i)];
      if (pair) openCompose({ mode: 'reply', to: vm.email || '', body: pair[1] });
    }));
  }

  return { close };
}

// =============================================================================
// THE LIST (artboard 1b) — the correspondence entry pattern, reusable.
// renderMailList(container, entries, { onOpen }) — entries in the shared
// normalised shape (mail-correspondence normArchiver / normManual, or built by
// the caller). One column; left rule navy=Received / grey=Sent; bucketed
// This week / Last week / Earlier. Clicking an entry calls onOpen(entry).
// =============================================================================
export function renderMailList(container, entries, { onOpen } = {}) {
  const timeOf = (d) => (d ? d.getTime() : 0);
  const sorted = entries.slice().sort((a, b) => timeOf(b.date) - timeOf(a.date));
  if (!sorted.length) {
    container.innerHTML = '<p class="g-note">No emails to show.</p>';
    return;
  }
  const now = new Date();
  const thisWeek = startOfWeek(now);
  const lastWeek = new Date(thisWeek); lastWeek.setDate(lastWeek.getDate() - 7);
  const buckets = { 'This week': [], 'Last week': [], 'Earlier': [] };
  sorted.forEach((e) => {
    const t = e.date ? e.date.getTime() : 0;
    if (t >= thisWeek.getTime()) buckets['This week'].push(e);
    else if (t >= lastWeek.getTime()) buckets['Last week'].push(e);
    else buckets['Earlier'].push(e);
  });
  const order = ['This week', 'Last week', 'Earlier'].filter((k) => buckets[k].length);
  const flat = order.reduce((arr, k) => arr.concat(buckets[k]), []);
  container.innerHTML = `<div style="display:flex;flex-direction:column;gap:22px;">${order.map((k) => bucketHtml(k, buckets[k])).join('')}</div>`;
  // The DOM order of .mc-entry matches `flat` (buckets render in order).
  container.querySelectorAll('.mc-entry').forEach((el, i) => {
    const e = flat[i];
    el.style.cursor = 'pointer';
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    const open = () => { if (typeof onOpen === 'function') onOpen(e); };
    el.addEventListener('click', open);
    el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } });
    el.addEventListener('mouseenter', () => { el.style.background = 'var(--brand-grey-50, #FBFBFA)'; });
    el.addEventListener('mouseleave', () => { el.style.background = ''; });
  });
}

// =============================================================================
// THE ROWS (Karan 08/10, project detail Emails tab) — the compact sibling of
// renderMailList: one bordered card per bucket, one row per email
// (Received/Sent label · sender · address · date / subject + one-line preview),
// and automatic replies (out of office etc.) folded into one "Auto · n" row
// with Show/Hide so they stop crowding the real correspondence.
// Returns { total, auto } for the caller's header count.
// =============================================================================
const AUTO_RE = /^\s*(automatic reply|auto[- ]?reply|autoreply|auto[- ]?response|out of (the )?office|ooo\b|away from (the )?office|on leave)/i;
const OOO_RE = /out of (the )?office|\booo\b|away from (the )?office|on leave/i;
export function isAutoReply(e) {
  const subj = String(e.subject || '').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '');
  return AUTO_RE.test(subj) || /^\s*automatic reply\s*:/i.test(String(e.subject || ''));
}
const autoKind = (e) => (OOO_RE.test(String(e.subject || '')) ? 'out of office' : 'automatic reply');

function previewLine(e) {
  return String(bodyPara(e) || '').replace(/\s+/g, ' ').trim();
}

function rowHtml(e, i) {
  const received = e.dir === 'Received';
  let detail = '';
  if (!received && e.to) detail = 'to ' + e.to;
  else if (received && e.email && e.email.toLowerCase() !== String(e.senderName || '').toLowerCase()) detail = e.email;
  const prev = previewLine(e);
  return `
    <div class="mrw-row" data-i="${i}" role="button" tabindex="0">
      <span class="mrw-dir${received ? '' : ' sent'}">${received ? 'Received' : 'Sent'}</span>
      <div class="mrw-main">
        <div class="mrw-l1">
          <span class="mrw-name">${escapeHtml(e.senderName || e.email || '')}</span>
          ${detail ? `<span class="mrw-detail">${escapeHtml(detail)}</span>` : ''}
          <span class="mrw-date">${escapeHtml(stamp(e.date))}</span>
        </div>
        <div class="mrw-l2"><span class="mrw-subj">${escapeHtml(e.subject)}</span>${prev ? ` <span class="mrw-prev">${escapeHtml(prev)}</span>` : ''}</div>
      </div>
    </div>`;
}

export function renderMailRows(container, entries, { onOpen } = {}) {
  const timeOf = (d) => (d ? d.getTime() : 0);
  const sorted = entries.slice().sort((a, b) => timeOf(b.date) - timeOf(a.date));
  if (!sorted.length) {
    container.innerHTML = '<p class="g-note">No emails to show.</p>';
    return { total: 0, auto: 0 };
  }
  const thisWeek = startOfWeek(new Date());
  const lastWeek = new Date(thisWeek); lastWeek.setDate(lastWeek.getDate() - 7);
  const buckets = { 'This week': [], 'Last week': [], 'Earlier': [] };
  sorted.forEach((e) => {
    const t = timeOf(e.date);
    if (t >= thisWeek.getTime()) buckets['This week'].push(e);
    else if (t >= lastWeek.getTime()) buckets['Last week'].push(e);
    else buckets['Earlier'].push(e);
  });
  const flat = [];
  let autoTotal = 0;
  const html = ['This week', 'Last week', 'Earlier'].filter((k) => buckets[k].length).map((k) => {
    const autos = buckets[k].filter(isAutoReply);
    const real = buckets[k].filter((e) => !isAutoReply(e));
    autoTotal += autos.length;
    const idx = (e) => { flat.push(e); return flat.length - 1; };
    let group = '';
    if (autos.length) {
      const names = autos.map((e) => `${e.senderName || e.email || 'Someone'} — ${autoKind(e)}`).join(' · ');
      group = `
      <div class="mrw-auto">
        <span class="mrw-pill">Auto · ${autos.length}</span>
        <span class="mrw-autonames">${escapeHtml(names)}</span>
        <button type="button" class="mrw-show" aria-expanded="false">Show</button>
      </div>
      <div class="mrw-autorows" hidden>${autos.map((e) => rowHtml(e, idx(e))).join('')}</div>`;
    }
    return `
    <section class="mrw-bucket">
      <div class="eyebrow blue mrw-label">${escapeHtml(k)}</div>
      <div class="mrw-card">${group}${real.map((e) => rowHtml(e, idx(e))).join('')}</div>
    </section>`;
  }).join('');
  container.innerHTML = `<div class="mrw">${html}</div>`;
  container.querySelectorAll('.mrw-row').forEach((el) => {
    const e = flat[Number(el.dataset.i)];
    const open = () => { if (typeof onOpen === 'function') onOpen(e); };
    el.addEventListener('click', open);
    el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } });
  });
  container.querySelectorAll('.mrw-show').forEach((b) => b.addEventListener('click', () => {
    const rows = b.closest('.mrw-auto').nextElementSibling;
    const show = rows.hidden;
    rows.hidden = !show;
    b.textContent = show ? 'Hide' : 'Show';
    b.setAttribute('aria-expanded', String(show));
  }));
  return { total: sorted.length, auto: autoTotal };
}

// entryHtml is re-exported for callers that need a single row outside a bucket.
export { entryHtml };
// One compact email row, for the project's Correspondence timeline (wa-timeline.js),
// which mixes these with WhatsApp rows in the same buckets.
export { rowHtml as mailRowHtml };
